import {
  getRelativePosition,
  useGetExhibition,
  useUpdateExhibition,
} from "@/api/exhibition/exhibition";
import AddFileModal from "@/components/modal/AddFileModal";
import { Exhibition } from "@/lib/types";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  appendNoDuplicateExhibitionNumberRefine,
  exhibitionSchema,
} from "@/api/exhibition/exhibition-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { formatFormRegistrationReference } from "@/lib/form-registration-header";
import { computeFieldExhibitionSummary } from "@/lib/computeFields";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { OtherAuthorsTable } from "./OtherAuthorsTable";

const schema = appendNoDuplicateExhibitionNumberRefine(
  exhibitionSchema.passthrough(),
);

const EditExhibition = ({ exhibition }: { exhibition: Exhibition }) => {
  // Rafraîchit le tableau des fichiers après un ajout réussi dans AddFileModal.
  const [refreshFiles, setRefreshFiles] = useState(false);

  const { instanceData } = useAuth();

  const form = useForm({
    defaultValues: exhibition,
    reValidateMode: "onSubmit",
    resolver: standardSchemaResolver(schema),
  });

  const isModulePhotoEnabled = useMemo(() => {
    return instanceData?.isModulePhotoEnabled;
  }, [instanceData?.isModulePhotoEnabled]);

  const isModuleSculptureEnabled = useMemo(() => {
    return instanceData?.isModuleSculptureEnabled;
  }, [instanceData?.isModuleSculptureEnabled]);

  const { data: attachments } = useGetAttachmentsByDocId(exhibition.id);
  const [, setSearchParam] = useSearchParams();

  const { t, i18n } = useTranslation();

  const exhibitionUpdater = useUpdateExhibition();

  const { relativePosition } = useDashboardParams<Exhibition>({
    statusField: "expositionsPublicationStatus",
    resourceName: "exhibition",
    relativePosition: {
      currentId: exhibition.id,
      positionFetcher: getRelativePosition,
    },
  });

  const handleResume = (data: Exhibition) => {
    return data
      ? computeFieldExhibitionSummary(data, { language: getMainLanguage() })
      : "";
  };

  const queryClient = useQueryClient();

  const expositionsDescriptionNumeroExpo = form.watch(
    "expositionsDescriptionNumeroExpo",
  );
  const exhibitionLabel = t("backoffice.header.exhibitionNumber.label");
  const registrationReference = useMemo(
    () => formatFormRegistrationReference(expositionsDescriptionNumeroExpo, {
      label: exhibitionLabel,
      locale: i18n.language,
    }),
    [expositionsDescriptionNumeroExpo, exhibitionLabel, i18n.language],
  );

  return (
    <FormLayout
      formKey="exhibition"
      title={exhibition.expositionsDescriptionTitre}
      id={exhibition.id}
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["exhibitions"],
        });
        queryClient.invalidateQueries({
          queryKey: ["exhibition", exhibition.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: exhibition.id }],
        });
      }}
      publicationStatus={exhibition.expositionsPublicationStatus}
      reference={registrationReference}
      picturesData={
        attachments && { pictures: attachments, type: "exhibition" }
      }
      relativePosition={relativePosition}
      type="exhibition"
      onSave={(status) => {
        form.handleSubmit(
          (values) => {
            exhibitionUpdater.mutate(
              {
                ...(values as Exhibition),
                ...(status && { expositionsPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("exhibitionSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.ventesDescriptionNumeroVente != undefined)
              setSearchParam({ tab: "description" });
          },
        )();
      }}
      form={form}
    >
      <AF.AbstractForm
        form={form}
        formKey="exhibition"
        tabs={
          [
            { nonHideable: true,
              value: "description",
              label: t("exhibitionDescription"),
            },
            {
              value: "other-authors",
              label: t("tab_expositionsDescriptionAutresArtistes"),
            },
            {
              value: "notes",
              label: t("exhibitionNotes"),
            },
            {
              value: "sources",
              label: t("sources"),
            },
            {
              value: "files",
              label: t("fichiers"),
            },
            {
              value: "links",
              label: t("linkedEntries"),
            },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Text name="expositionsDescriptionNumeroExpo" nonMaskable />
            <AF.Row>
              <AF.Text name="expositionsDescriptionAnnee" />
              <AF.Checkbox
                className="w-fit pt-6"
                name="expositionsDescriptionAnneeIncertaine"
                align
              />
            </AF.Row>
            <AF.Text name="expositionsDescriptionTitre" nonMaskable />
            <AF.Checkbox
              className="w-fit"
              name="expositionsDescriptionTitreIncertain"
            />
            <AF.Text name="expositionsDescriptionTitreDOrigine" />
            <Separator />
            <AF.Text name="expositionsDescriptionDates" />
            <AF.RadioGroup
              resettable
              items={[
                { value: "expositionsDescriptionDatesChoixIncertaines" },
                { value: "expositionsDescriptionDatesChoixInconnues" },
              ]}
            />
            <Separator />
            <AF.Text name="expositionsDescriptionLieu" />
            <AF.Checkbox
              className="w-fit"
              name="expositionsDescriptionLieuIncertain"
            />
            <ModifiableList
              name="expositionsDescriptionVille"
              listKey="liste-villes"
            />
            <AF.Checkbox name="expositionsDescriptionCityIncertain" />
            <ModifiableList
              name="expositionsDescriptionPays"
              listKey="liste-pays"
            />
            <AF.Checkbox name="expositionsDescriptionCountryIncertain" />
          </AF.Col>
          <AF.Col>
            <AF.Text name="expositionsDescriptionTexteSupplementaire" />
            <AF.Text name="expositionsDescriptionOrganisateur" />
            <AF.RichText
              name="expositionsDescriptionResume"
              computeAction={handleResume}
            />
            <AF.Checkbox name="expositionsDescriptionExpositionItinerante" />
            <AF.Checkbox name="expositionsDescriptionExpositionDansMusee" />
            <AF.Checkbox name="expositionsDescriptionExpositionCollective" />
            <AF.Checkbox name="expositionsDescriptionExpositionPersonnelle" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="other-authors">
          <AF.Col>
            <OtherAuthorsTable exhibitionId={exhibition.id} />
            <AF.RichText name="expositionsDescriptionAutresArtistes" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="notes">
          <AF.RichText name="expositionsDescriptionNotes" />
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={
            <AddFileModal
              id={exhibition.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            docId={exhibition.id}
            name="diversfiles"
            refreshFiles={refreshFiles}
            setRefreshFiles={setRefreshFiles}
          />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={exhibition.id}
            type="exhibition"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "artworkInExhibitions",
                      label: t("entryartworks"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t("exhibitionDescriptionNumeroExpo"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t("exhibitionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansExpositionsDescriptionNumeroCatalogue",
                          label: t(
                            "oeuvresDansExpositionsDescriptionNumeroCatalogue",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansExpositionsNotes",
                          label: t("oeuvresDansExpositionsNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "multipleprintInExhibition",
                      label: t("entrymultipleprints"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionNumeroExpo",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionResume",
                          ),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "expositionsDansEstampesMultiplesDescriptionNumero",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionNumero",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionExemplaireExpose",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "sculptureEditionInExhibitions",
                      label: t("linkedSculptureEditions"),
                      type: "link-complex",
                      hidden: !isModuleSculptureEnabled,
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t("exhibitionDescriptionNumeroExpo"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t("exhibitionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "sculptureEditionDansExpositionsNumeroCatalogue",
                          label: t(
                            "sculptureEditionDansExpositionsNumeroCatalogue",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansExpositionsNotes",
                          label: t("sculptureEditionDansExpositionsNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "multipleSampleInExhibitions",
                      label: t("entrymultiplesamples"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t("exhibitionDescriptionNumeroExpo"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t("exhibitionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "multiplesampleDansExpositionsDescriptionNumeroCatalogue",
                          label: t(
                            "multiplesampleDansExpositionsDescriptionNumeroCatalogue",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansExpositionsNotes",
                          label: t("multiplesampleDansExpositionsNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "tirageInExhibition",
                      label: t("entrytirages"),
                      type: "link-complex",
                      hidden: !isModulePhotoEnabled,
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t("exhibitionDescriptionNumeroExpo"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t("exhibitionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "tirageDansExpositionsDescriptionNumeroCatalogue",
                          label: t(
                            "tirageDansExpositionsDescriptionNumeroCatalogue",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansExpositionsNotes",
                          label: t("tirageDansExpositionsNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedBibliography",
                      label: t("entrybibliographies"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedPhotography",
                      label: t("entryphotographies"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
            data={exhibition}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditExhibitionPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetExhibition(id);

  return (
    <>
      {data && <EditExhibition key={data.id} exhibition={data} />}

      {!data && !isLoading && (
        <h2 className="p-4">expositionNExistePasLabel</h2>
      )}
    </>
  );
};
